import { jsx as _jsx } from "react/jsx-runtime";
import { clearPersistedRecentImports } from '@/hooks/use-import';
import { login as apiLogin, logout as apiLogout, clearAuth, getAccessToken, refreshToken, } from '@/lib/api';
import { setAccessToken, setActiveRegionOverride, setCsrfToken } from '@/lib/api/client';
import { forgetClinicFilter } from '@/lib/clinicFilterMemory';
import { clearEchoPanelMemory } from '@/lib/echo/echoPanelMemory';
import { clearLoginEmail } from '@/lib/loginEmailMemory';
import { multiScreenService } from '@/lib/multiScreen/multiScreenService';
import { windowChannel } from '@/lib/multiScreen/windowChannel';
import { isPublicRoute } from '@/lib/publicRoutes';
import { queryClient } from '@/lib/queryClient';
import { clearAllCaptureBuffers } from '@/lib/report/captureBuffer';
import { viewerSettingsService } from '@/lib/settings/viewerSettingsService';
import { clearTcReturn, clearWorklistReturn } from '@/lib/studyNavigation';
import { clearRecentClinics } from '@/lib/teleconsultation/sidebarPrefs';
import { clearTcWorklistState } from '@/lib/teleconsultation/worklistUrlState';
import { clearOrientationOverrideMemory } from '@/lib/viewer/orientationOverrideMemory';
import { clearPresentationMemory } from '@/lib/viewer/presentationMemory';
import { createContext, useCallback, useContext, useEffect, useReducer, useRef, } from 'react';
import { useLocation } from 'wouter';
// DEV MODE: Set to true to bypass authentication
const DEV_MODE = false;
const USER_EMAIL_KEY = 'userEmail';
// Shared-workstation hygiene: no session memory (login email, worklist
// reading position, TC reading position + worklist view, clinic filter, open
// echo panels, viewer presentation + MPR orientation corrections, TC
// recent-clinic history) may outlive a session.
// Runs on logout AND on every login success (login + establishSession): the
// login-side sweep covers sessions that never ended cleanly — a 401 redirect,
// a killed browser, or the viewer's unmount cleanups re-saving presentation
// records AFTER logout's synchronous clear (setLocation('/login') unmounts the
// viewer, whose cleanup effects write to sessionStorage again).
//
// Membership is decided by what the record REVEALS, not by which Storage it
// lives in: clearRecentClinics() wipes localStorage because the list records
// which hospitals a reader was working in, and a shared workstation must not
// carry that into the next radiologist's rail. Impersonal UI chrome (the TC
// sidebar's section open/closed state, 'tc.sidebar.sections') is deliberately
// left untouched — it reveals nothing about the previous user.
function clearPerTabSessionMemory() {
    // Buffered viewport captures are patient images, and they now live in
    // localStorage so a capture can reach a report in another WINDOW. Sweeping
    // here rather than only in logout() is what makes that safe: logout is not the
    // only way a session ends. An expired refresh token redirects to /login through
    // handleUnauthorized without React ever running logout, and a killed tab or a
    // crash runs nothing at all — in both cases this sweep still fires on the next
    // sign-in, before the new reader can see anything.
    clearAllCaptureBuffers();
    clearLoginEmail();
    clearWorklistReturn();
    clearTcReturn();
    clearTcWorklistState();
    clearEchoPanelMemory();
    clearPresentationMemory();
    clearOrientationOverrideMemory();
    forgetClinicFilter();
    clearRecentClinics();
}
function loadStoredEmail() {
    if (typeof window === 'undefined')
        return null;
    try {
        return localStorage.getItem(USER_EMAIL_KEY);
    }
    catch {
        return null;
    }
}
function persistEmail(email) {
    if (typeof window === 'undefined')
        return;
    try {
        if (email) {
            localStorage.setItem(USER_EMAIL_KEY, email);
        }
        else {
            localStorage.removeItem(USER_EMAIL_KEY);
        }
    }
    catch {
        // Ignore storage errors (e.g. privacy mode)
    }
}
function authReducer(_state, action) {
    switch (action.type) {
        case 'INIT':
            return { isAuthenticated: action.isAuthenticated, isLoading: false, userEmail: action.email };
        case 'LOGIN':
            return { isAuthenticated: true, isLoading: false, userEmail: action.email };
        case 'LOGOUT':
            return { isAuthenticated: false, isLoading: false, userEmail: null };
    }
}
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, undefined, () => ({
        isAuthenticated: DEV_MODE,
        isLoading: !DEV_MODE,
        userEmail: loadStoredEmail(),
    }));
    const [, setLocation] = useLocation();
    // Check for existing auth on mount
    useEffect(() => {
        const storedEmail = loadStoredEmail();
        // Skip auth check in dev mode
        if (DEV_MODE) {
            dispatch({ type: 'INIT', isAuthenticated: true, email: storedEmail });
            return;
        }
        // Public routes must not trigger a silent refresh — a 401 would redirect
        // unauthenticated visitors away before the page can render (share links).
        if (isPublicRoute(window.location.pathname)) {
            dispatch({ type: 'INIT', isAuthenticated: false, email: storedEmail });
            return;
        }
        const checkAuth = async () => {
            // Try to refresh token if we don't have one
            if (!getAccessToken()) {
                try {
                    await refreshToken();
                    viewerSettingsService.initFromBackend();
                    multiScreenService.initFromBackend();
                    dispatch({ type: 'INIT', isAuthenticated: true, email: storedEmail });
                }
                catch {
                    // No valid session
                    dispatch({ type: 'INIT', isAuthenticated: false, email: storedEmail });
                }
            }
            else {
                viewerSettingsService.initFromBackend();
                dispatch({ type: 'INIT', isAuthenticated: true, email: storedEmail });
            }
        };
        checkAuth();
    }, []);
    const login = useCallback(async (credentials) => {
        const response = await apiLogin(credentials);
        // MFA-enrolled identity: no session was issued; the page renders the
        // code step and completes via establishSession() after /auth/mfa/verify.
        if ('mfaRequired' in response) {
            return { mfaRequired: true, challengeToken: response.challengeToken };
        }
        queryClient.clear();
        persistEmail(credentials.email);
        // New session: sweep every per-tab memory (incl. the login-screen email,
        // which has served its purpose — the MFA branch sweeps later, via
        // establishSession) so nothing from a previous session is inherited.
        clearPerTabSessionMemory();
        viewerSettingsService.initFromBackend();
        dispatch({ type: 'LOGIN', email: credentials.email });
        setLocation('/');
        return { mfaRequired: false };
    }, [setLocation]);
    const logout = useCallback(async (options) => {
        // Only the window where the user clicked Log out revokes the refresh
        // token; siblings just drop local state (skipBroadcast) — a second
        // apiLogout would 401 against the already-revoked cookie.
        if (!options?.skipBroadcast) {
            windowChannel.logout();
            try {
                await apiLogout();
            }
            catch {
                // A rejected revoke (stale CSRF token, network) must not strand the
                // user signed in locally; the refresh cookie expires on its own.
            }
        }
        queryClient.clear();
        viewerSettingsService.resetForLogout();
        multiScreenService.resetForLogout();
        clearPersistedRecentImports();
        persistEmail(null);
        // Best-effort immediate sweep. The viewer (if mounted) unmounts on the
        // redirect below and its cleanup effects re-save presentation records —
        // the login-side sweep in clearPerTabSessionMemory() is what guarantees
        // the NEXT session starts clean regardless.
        clearPerTabSessionMemory();
        clearAuth();
        dispatch({ type: 'LOGOUT' });
        if (!options?.skipRedirect) {
            setLocation('/login');
        }
    }, [setLocation]);
    // Multi-monitor: a logout anywhere logs out every window. Popout viewer and
    // report windows show PHI and hold in-memory access tokens — they must not
    // outlive the session that spawned them.
    const logoutRef = useRef(logout);
    useEffect(() => {
        logoutRef.current = logout;
    });
    useEffect(() => {
        return windowChannel.on('LOGOUT', () => {
            void logoutRef.current({ skipBroadcast: true });
        });
    }, []);
    // Set the session as authenticated using tokens that another flow already obtained
    // (e.g. invite-accept). The endpoint set the refresh-token cookie server-side; we still
    // need to push the access token + CSRF into the api client's in-memory cache here, the
    // same way apiLogin does — otherwise the next protected request fires without auth and
    // gets bounced to /login.
    const establishSession = useCallback(({ email, accessToken, csrfToken }) => {
        setAccessToken(accessToken.value, accessToken.expiresAt);
        setCsrfToken(csrfToken);
        // Shared-machine hygiene: clear any stale super-admin region override so the
        // newly-accepted invite user starts at their own default region.
        setActiveRegionOverride(null);
        queryClient.clear();
        persistEmail(email);
        // A session is established (covers every entry path: invite-accept, SSO,
        // MFA) — sweep all per-tab memory so nothing from a previous session
        // (however it ended) is inherited.
        clearPerTabSessionMemory();
        viewerSettingsService.initFromBackend();
        dispatch({ type: 'LOGIN', email });
    }, []);
    return (_jsx(AuthContext.Provider, { value: { ...state, login, logout, establishSession }, children: children }));
}
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
